import { useMemo, useState } from 'react';
import { Monitor, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import { controlClass, primaryButtonClass } from '../components/FilterBar';
import { get, post } from '../lib/api';

interface McpTool {
  name: string;
  description: string;
  method: 'GET' | 'POST';
  path: string;
  sample: Record<string, unknown>;
}

const TOOLS: McpTool[] = [
  { name: 'get_dashboard_summary', description: 'Overall dashboard summary.', method: 'GET', path: '/v1/dashboard/summary', sample: {} },
  { name: 'list_transactions', description: 'Search unified transactions.', method: 'GET', path: '/v1/transactions/search', sample: { query: '', status: 'all', limit: 25 } },
  { name: 'list_invoices', description: 'List invoices from SolidInvoice.', method: 'GET', path: '/v1/invoices', sample: { limit: 20 } },
  { name: 'list_unpaid_invoices', description: 'List unpaid invoices.', method: 'GET', path: '/v1/invoices/unpaid', sample: { limit: 50 } },
  { name: 'find_contracts', description: 'Find contracts in Documenso.', method: 'GET', path: '/v1/contracts/find', sample: { query: '', status: 'all' } },
  { name: 'get_mercury_transactions', description: 'List Mercury transactions.', method: 'GET', path: '/v1/mercury/transactions', sample: { limit: 50 } },
  { name: 'get_paypal_transactions', description: 'List PayPal transactions.', method: 'GET', path: '/v1/paypal/transactions', sample: { limit: 50 } },
  { name: 'get_chain2pay_payments', description: 'List Chain2Pay payments.', method: 'GET', path: '/v1/chain2pay/payments', sample: { status: 'all', limit: 50 } },
  { name: 'get_wallet_balances', description: 'Crypto wallet balances.', method: 'GET', path: '/v1/wallets/balances', sample: { wallet_type: 'all' } },
  { name: 'get_asset_register', description: 'Fixed asset register.', method: 'GET', path: '/v1/assets/register', sample: {} },
  { name: 'get_tax_estimate', description: 'Quarterly tax estimate.', method: 'GET', path: '/v1/tax/estimate', sample: { year: new Date().getFullYear(), quarter: 'Q1' } },
  { name: 'search_documents', description: 'Search Paperless / oCIS documents.', method: 'GET', path: '/v1/documents/search', sample: { q: 'receipt', limit: 20 } },
  { name: 'web_search', description: 'Perform a web/internal search.', method: 'POST', path: '/v1/search/web', sample: { query: 'zerotail', limit: 5 } },
  { name: 'list_webhooks', description: 'List inbound webhook events.', method: 'GET', path: '/v1/webhooks', sample: { page: 1, page_size: 20 } },
];

function buildQueryString(args: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(args)) {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function McpConsole() {
  const [toolName, setToolName] = useState(TOOLS[0].name);
  const [payload, setPayload] = useState(JSON.stringify(TOOLS[0].sample, null, 2));
  const [result, setResult] = useState('');
  const [running, setRunning] = useState(false);
  const [ok, setOk] = useState<boolean | null>(null);

  const tool = useMemo(() => TOOLS.find((t) => t.name === toolName) ?? TOOLS[0], [toolName]);

  const handleToolChange = (name: string) => {
    setToolName(name);
    const next = TOOLS.find((t) => t.name === name);
    if (next) setPayload(JSON.stringify(next.sample, null, 2));
    setResult('');
    setOk(null);
  };

  const handleRun = async () => {
    let args: Record<string, unknown> = {};
    if (payload.trim()) {
      try {
        args = JSON.parse(payload);
      } catch {
        toast.error('Arguments must be valid JSON');
        return;
      }
    }

    setRunning(true);
    setOk(null);
    const response =
      tool.method === 'GET'
        ? await get(`${tool.path}${buildQueryString(args)}`, { suppressToast: true })
        : await post(tool.path, args, { suppressToast: true });
    setRunning(false);
    setOk(response.ok);

    if (response.ok) {
      setResult(JSON.stringify(response.data, null, 2));
    } else {
      setResult(JSON.stringify(response.error, null, 2));
      toast.error(response.error?.message ?? 'Request failed');
    }
  };

  return (
    <PageShell
      title="MCP Console"
      subtitle="Exercise MCP tools against their live API endpoints and inspect the raw JSON response."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-zt-border bg-zt-surface p-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Tool</label>
            <select value={toolName} onChange={(e) => handleToolChange(e.target.value)} className={`w-full ${controlClass}`}>
              {TOOLS.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-zt-muted">{tool.description}</p>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="rounded bg-zt-elevated px-2 py-1 font-mono text-zt-muted">{tool.method}</span>
            <span className="truncate font-mono text-zt-muted">{tool.path}</span>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">
              Arguments (JSON) — sent as {tool.method === 'GET' ? 'query params' : 'request body'}
            </label>
            <textarea
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              rows={8}
              spellCheck={false}
              className={`w-full font-mono ${controlClass}`}
            />
          </div>

          <button type="button" onClick={handleRun} disabled={running} className={primaryButtonClass}>
            <Play size={16} /> {running ? 'Running…' : 'Run tool'}
          </button>
        </div>

        <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Monitor className="text-zt-primary" size={18} />
              <h3 className="font-semibold text-white">Response</h3>
            </div>
            {ok !== null && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  ok ? 'bg-zt-primary/10 text-zt-primary' : 'bg-zt-danger/10 text-zt-danger'
                }`}
              >
                {ok ? 'Success' : 'Error'}
              </span>
            )}
          </div>
          <pre className="h-[28rem] overflow-auto rounded-lg bg-zt-bg p-4 font-mono text-xs text-zt-muted">
            {result || '// Run a tool to see the response here.'}
          </pre>
        </div>
      </div>
    </PageShell>
  );
}

export default McpConsole;
